import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import type { User, LoginCredentials, AuthResponse } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials, adminAccessToken?: string) => Promise<void>;
  loginWithGoogle: (credentialOrCode: string, isCode?: boolean, redirectUri?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isManager: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      localStorage.removeItem('authToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials, adminAccessToken?: string) => {
    try {
      const response: AuthResponse = await authAPI.login(credentials, adminAccessToken);
      localStorage.setItem('authToken', response.token);
      setUser(response.user);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const loginWithGoogle = async (credentialOrCode: string, isCode: boolean = false, redirectUri?: string) => {
    try {
      console.log('[AuthContext] loginWithGoogle called:', {
        isCode,
        redirectUri,
        credentialOrCodeLength: credentialOrCode?.length,
      });
      const response = await authAPI.googleLogin(credentialOrCode, isCode, redirectUri);
      console.log('[AuthContext] Google login response received:', {
        hasToken: !!response.token,
        hasUser: !!response.user,
        hasCalendarAccess: response.has_calendar_access,
      });
      localStorage.setItem('authToken', response.token);
      setUser(response.user);
      
      // Show message if Calendar access was granted
      if (response.has_calendar_access) {
        console.log('✅ Google Calendar connected with refresh token!');
      } else {
        console.log('⚠️ Google Calendar not connected (no refresh token)');
      }
    } catch (error: any) {
      console.error('[AuthContext] Google login failed:', error);
      console.error('[AuthContext] Error details:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data,
        responseStatus: error.response?.status,
        stack: error.stack,
      });
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setUser(null);
      window.location.href = '/login';
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isOwner: user?.role === 'owner',
    isAdmin: user?.role === 'owner' || user?.role === 'admin',
    isManager: user?.role === 'manager',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

